/* eslint-disable no-param-reassign */
/* eslint-disable indent */
import {
  Chip,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import LanguageIcon from '@material-ui/icons/Language';
import WarningIcon from '@material-ui/icons/Warning';
import clsx from 'clsx';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { ListAllowedIpsDataProps } from 'modules/clients/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { cyrb53Hash, regexExp } from 'utils/helpers';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import styles from './styles';

const useStyles = makeStyles(styles);

interface FormAllowedIpsProps {
  active?: boolean;
  loading?: boolean;
  allowedIps: ListAllowedIpsDataProps | null;
  role?: RolesEnum;
  companySelected: CompanyData | null;
  permissions: PermissionsData | undefined;
  onChange: (value: string[], status: boolean) => void;
  onError: (error: boolean) => void;
}

const FormAllowedIps = ({
  active,
  loading,
  allowedIps,
  permissions,
  role,
  companySelected,
  onChange,
  onError,
}: FormAllowedIpsProps) => {
  const classes = useStyles();
  const [hasError, setHasError] = React.useState(false);
  const [defaultValue, setDefaultValue] = React.useState<string[]>([]);
  const [isActive, setIsActive] = React.useState<boolean>(!!active);

  React.useEffect(() => {
    setIsActive(!!active);
  }, [active]);

  const handleChange = (newValues: string[]) => {
    const value = newValues.filter(
      (value, index) => newValues.indexOf(value) === index
    );

    const valueFormat = value.map(item => {
      if (item.includes('.')) return item;

      const first = item.slice(0, 2);
      const second = item.slice(2, 5);
      const third = item.slice(5, 7);
      const four = item.slice(7, 10);

      return `${first}.${second}.${third}.${four}`;
    });

    onChange(valueFormat, isActive);
    setDefaultValue(valueFormat);

    const invalidIps = valueFormat.some(
      item => !regexExp.ipNumberWithSlash?.test(item)
    );
    const error = isActive && (valueFormat.length === 0 || invalidIps);

    setHasError(error);
  };

  const handleRemove = (value: string) => {
    const newValue = defaultValue.filter(item => item !== value);
    handleChange(newValue);
  };

  const renderTags = (values: string[]) => {
    if (values.length === 0) return <></>;

    const tags = values.map((label: string) => ({
      label,
      isValidIP: regexExp.ipNumberWithSlash?.test(label),
    }));

    return tags.map((option: { label: string; isValidIP: boolean }) => (
      <Chip
        key={cyrb53Hash(option?.label)}
        classes={
          !option?.isValidIP
            ? {
                outlined: classes.chipError,
                icon: classes.chipIcon,
                deleteIcon: classes.chipDeleteIcon,
              }
            : {}
        }
        icon={
          !option?.isValidIP ? (
            <WarningIcon
              className={clsx({
                [classes.iconGreen]: inAdminView(role, companySelected),
                [classes.iconGray]: inSuperAdminView(role, companySelected),
                [classes.iconBlue]: inOperatorView(role, companySelected),
                [classes.iconYellow]: companySelected,
              })}
              fontSize="small"
            />
          ) : (
            <></>
          )
        }
        variant="outlined"
        label={option?.label}
        onDelete={() => handleRemove(option?.label)}
      />
    ));
  };

  const handleOnBlurChange = event => {
    if (event?.target?.value?.trim() !== '') {
      handleChange([...defaultValue, event?.target?.value]);
    }
    event.target.value = '';
  };

  React.useEffect(() => {
    onError(hasError);
  }, [onError, hasError]);

  React.useEffect(() => {
    if (!isActive) setDefaultValue([]);
  }, [isActive]);

  React.useEffect(() => {
    if (allowedIps?.results) {
      const formattedIps = allowedIps.results.map(item => item?.ip_address);
      setDefaultValue(formattedIps);
      setHasError(
        isActive &&
          formattedIps.some(ip => !regexExp.ipNumberWithSlash?.test(ip))
      );
    }
  }, [allowedIps, isActive]);

  return (
    <ToggleCard
      disable={!!permissions && !permissions?.client_access_control}
      id="toggle-ip-restriction"
      active={isActive}
      title={i18n.t('BY_IP_KEY')}
      icon={
        <LanguageIcon
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
      getActive={status => {
        const newIsActive = !status;
        setIsActive(newIsActive);
        onChange(defaultValue, newIsActive);
        setHasError(
          newIsActive &&
            !!defaultValue.length &&
            defaultValue.some(ip => !regexExp.ipNumberWithSlash?.test(ip))
        );
      }}
    >
      <Grid className={classes.cardsContent}>
        {!loading && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="outlined-basic"
              placeholder={defaultValue.length <= 0 ? '000.000.0.0' : ''}
              label={i18n.t('RELEASED_IP_ADDRESSES_KEY')}
              variant="outlined"
              type="text"
              onBlur={handleOnBlurChange}
              className={clsx(classes.autocomplete, {
                [classes.autocompleteError]: hasError,
              })}
              InputProps={{
                startAdornment: renderTags(defaultValue),
                onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => {
                  const target = event.target as HTMLInputElement;
                  if (event.key === 'Enter' && target.value.trim() !== '') {
                    handleChange([...defaultValue, target.value]);
                    target.value = '';
                  }
                },
                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  const target = event.target as HTMLInputElement;
                  if (!Number(target.value) && !target.value.includes('.')) {
                    target.value = '';
                  }
                },
              }}
            />
          </Grid>
        )}

        {hasError && (
          <Typography
            variant="caption"
            color="error"
            className={classes.errorMessage}
          >
            {i18n.t('CLIENTS_PAGE.FIELD_INVALID_IP_ADDRESS_KEY')}
          </Typography>
        )}
      </Grid>
    </ToggleCard>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  clients: {
    accessControl: { loading, allowedIps },
  },
}: ApplicationState) => ({
  loading,
  allowedIps,
  permissions: data?.permissions,
});

export default connect(mapStateToProps)(FormAllowedIps);
